import {
    AddIcon,
    Button,
    ButtonIcon,
    ButtonSpinner,
    ButtonText,
    ChevronLeftIcon,
    Drawer,
    Heading,
    HStack,
    Modal,
    ModalLayout,
    Steps,
    Text,
    useAccounts,
    useListingService,
    useMobileView,
    useUserContext,
    VStack
} from "@hosttools/frontend";
import type {AccountType} from "@hosttools/frontend/models/account";
import type React from "react";
import {memo, useCallback, useEffect, useMemo, useState} from "react";

import AccountList from "./AccountList";

import ModalAddAccount from "@/admin/components/ModalAddAccount";

type Props = {
    accountType: AccountType;
    listingID?: string;
    oAuthReturnUrl: string;
    oAuthStateParameter?: string;
    onClose: () => void;
    onImportListingsSuccess: () => void;
};

const ModalImportListings: React.FC<Props> = ({
    accountType,
    listingID,
    oAuthReturnUrl,
    oAuthStateParameter,
    onClose,
    onImportListingsSuccess
}) => {
    const {accounts} = useUserContext();
    const isMobile = useMobileView();
    const {channels, badChannels} = useAccounts(accounts);
    const listingService = useListingService();

    const [showModalAddAccount, setShowModalAddAccount] = useState<boolean>(false);
    const [hasAccountConnected, setHasAccountConnected] = useState<boolean>(false);
    const [spinner, setSpinner] = useState(false);
    const [accountListings, setAccountListings] = useState<Map<string, string[]>>(new Map());

    const isEmpty = accountListings.size === 0;

    const handleImportListings = useCallback(async () => {
        try {
            setSpinner(true);

            if (isEmpty) {
                return;
            }

            // if listingID is provided, connect listing to existing listing
            if (listingID) {
                const {value} = accountListings.entries().next();
                if (value) {
                    const [accountID, [externalListingID]] = value;
                    await listingService.connectListingToAccount(
                        accountID,
                        listingID,
                        externalListingID
                    );
                }
            } else {
                await Promise.all(
                    [...accountListings].flatMap(async ([accountID, listingIDs]) => {
                        if (listingIDs.length) {
                            await listingService.importListings(accountID, listingIDs);
                        }
                    })
                );
            }
            onImportListingsSuccess();
        } finally {
            setSpinner(false);
        }
    }, [accountListings, isEmpty, listingID, listingService, onImportListingsSuccess]);

    const handleConnectAdditionalAccount = useCallback(() => {
        setShowModalAddAccount(true);
    }, []);

    const hideModalAddAccount = useCallback(
        (isSuccess?: boolean) => {
            setShowModalAddAccount(false);
            // close the modal as well, otherwise, it renders an null value for the modal
            if (typeof isSuccess === "undefined") {
                onClose();
            }
        },
        [onClose]
    );

    const handleAccountConnected = useCallback(() => {
        setHasAccountConnected(true);
    }, []);

    useEffect(() => {
        if (
            channels.some(channel => channel.type === accountType) ||
            badChannels.some(channel => channel.type === accountType)
        ) {
            setHasAccountConnected(true);
        } else {
            // show the modal when nothing is connected yet
            setShowModalAddAccount(true);
        }
    }, [accountType, badChannels, channels]);

    const footer = useMemo(() => {
        return (
            <HStack className="w-full justify-between">
                <Button
                    action="secondary"
                    variant="outline"
                    onPress={handleConnectAdditionalAccount}
                >
                    <ButtonIcon as={AddIcon} size="sm" />
                    <ButtonText>Account</ButtonText>
                </Button>
                <HStack space="sm">
                    <Button
                        action="secondary"
                        className="hidden md:block"
                        variant="outline"
                        onPress={onClose}
                    >
                        <ButtonText>Cancel</ButtonText>
                    </Button>
                    <Button
                        isDisabled={isEmpty}
                        testID="btn-import-listings"
                        onPress={handleImportListings}
                    >
                        {spinner && <ButtonSpinner />}
                        <ButtonText>{listingID ? "Connect" : "Import"}</ButtonText>
                    </Button>
                </HStack>
            </HStack>
        );
    }, [
        handleConnectAdditionalAccount,
        handleImportListings,
        isEmpty,
        listingID,
        onClose,
        spinner
    ]);

    if (showModalAddAccount) {
        return (
            <ModalAddAccount
                channel={accountType}
                modalSize={isMobile ? "xl" : "md"}
                oAuthReturnUrl={oAuthReturnUrl}
                oAuthStateParameter={oAuthStateParameter}
                responsive={false}
                onConnected={handleAccountConnected}
                onHide={hideModalAddAccount}
            />
        );
    }

    if (hasAccountConnected) {
        if (isMobile) {
            return (
                <Drawer isOpen onClose={onClose}>
                    <ModalLayout isStickyFooter footer={footer}>
                        <VStack>
                            <Button
                                no110MinWidth
                                action="default"
                                className="self-start md:hidden mb-8"
                                variant="ghost"
                                onPress={onClose}
                            >
                                <ButtonIcon as={ChevronLeftIcon} />
                                <ButtonText>Back</ButtonText>
                            </Button>
                            {!listingID && (
                                <Steps
                                    className="flex md:hidden w-fit mb-4"
                                    step={2}
                                    totalSteps={3}
                                />
                            )}
                            <VStack className="mb-6" space="sm">
                                <Heading variant={isMobile ? "md" : "2xl"}>
                                    Select listings to add to Host Tools
                                </Heading>
                                <Text isParagraph variant={isMobile ? "sm" : "lg"}>
                                    Import your listings from one of these channels or manually
                                    create a listing on Host Tools.
                                </Text>
                            </VStack>
                            <AccountList
                                accountListings={accountListings}
                                accountType={accountType}
                                multiSelect={!listingID}
                                onAddAdditionalAccount={handleConnectAdditionalAccount}
                                onChangeAccountListings={setAccountListings}
                                onReconnectAccount={handleConnectAdditionalAccount}
                            />
                        </VStack>
                    </ModalLayout>
                </Drawer>
            );
        }
        return (
            <Modal
                isOpen
                footer={footer}
                isLoading={spinner}
                size="md"
                title="Select listings to import"
                useAsDrawer={isMobile}
                onClose={onClose}
            >
                <AccountList
                    accountListings={accountListings}
                    accountType={accountType}
                    multiSelect={!listingID}
                    onAddAdditionalAccount={handleConnectAdditionalAccount}
                    onChangeAccountListings={setAccountListings}
                    onReconnectAccount={handleConnectAdditionalAccount}
                />
            </Modal>
        );
    }

    return null;
};

export default memo(ModalImportListings);
